import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { db } from '../../../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FiBell, FiClock, FiVolume2, FiLink, FiSave, FiCheck, FiSettings } from 'react-icons/fi';
import toast from 'react-hot-toast';
import WordPressConnectionCard from '../components/WordPressConnectionCard';

const ALERT_OPTIONS = [
    { value: 5, label: '5 minutos antes' },
    { value: 10, label: '10 minutos antes' },
    { value: 15, label: '15 minutos antes' },
    { value: 30, label: '30 minutos antes' },
    { value: 60, label: '1 hora antes' },
    { value: 120, label: '2 horas antes' },
];

const CalendarSettingsPage = () => {
    const { user } = useAuth();
    const [settings, setSettings] = useState({
        enabled: true,
        alerts: [30, 10],
        sound: true,
        showZoomLink: true,
    });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState('default');

    useEffect(() => {
        if (!user?.uid) return;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                const data = snap.data();
                if (data?.calendarNotifications) {
                    setSettings(data.calendarNotifications);
                }
            } catch { }
        })();
        if ('Notification' in window) {
            setPermissionStatus(Notification.permission);
        }
    }, [user?.uid]);

    const handleSave = async () => {
        if (!user?.uid) return;
        setSaving(true);
        try {
            await setDoc(doc(db, 'users', user.uid), { calendarNotifications: settings }, { merge: true });
            toast.success('Configuración guardada');
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const toggleAlert = (min) => {
        setSettings(prev => ({
            ...prev,
            alerts: prev.alerts.includes(min)
                ? prev.alerts.filter(a => a !== min)
                : [...prev.alerts, min].sort((a, b) => b - a)
        }));
    };

    const requestPermission = async () => {
        if ('Notification' in window) {
            const perm = await Notification.requestPermission();
            setPermissionStatus(perm);
            if (perm === 'granted') toast.success('Notificaciones activadas');
        }
    };

    return (
        <div className="max-w-2xl mx-auto py-8 px-4">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <FiSettings className="text-indigo-600" /> Ajustes del Calendario
                </h1>
                <p className="text-sm text-gray-500 mt-1">Conexión con WordPress y notificaciones de eventos</p>
            </div>

            <div className="space-y-6">
                {/* Conexión con WordPress */}
                <WordPressConnectionCard />

                <h2 className="text-sm font-bold text-gray-700 pt-2">Notificaciones de eventos</h2>

                {/* Permission status */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-gray-800 mb-3">Permisos del navegador</h3>
                    <div className="flex items-center gap-3">
                        <span className={`w-3 h-3 rounded-full ${permissionStatus === 'granted' ? 'bg-green-500' : permissionStatus === 'denied' ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <span className="text-sm text-gray-600">
                            {permissionStatus === 'granted' && 'Notificaciones activadas'}
                            {permissionStatus === 'denied' && 'Notificaciones bloqueadas (actívalas desde la configuración del navegador)'}
                            {permissionStatus === 'default' && 'Notificaciones pendientes de activar'}
                        </span>
                        {permissionStatus !== 'granted' && permissionStatus !== 'denied' && (
                            <button onClick={requestPermission}
                                className="ml-auto text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700">
                                Activar
                            </button>
                        )}
                    </div>
                </div>

                {/* Enable/Disable */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-sm font-bold text-gray-800">Notificaciones del calendario</h3>
                            <p className="text-xs text-gray-500 mt-0.5">Recibe alertas antes de cada clase o masterclass</p>
                        </div>
                        <button onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                            className={`w-11 h-6 rounded-full transition-colors relative ${settings.enabled ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.enabled ? 'left-[22px]' : 'left-0.5'}`} />
                        </button>
                    </div>
                </div>

                {settings.enabled && (
                    <>
                        {/* Alert times */}
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-2">
                                <FiClock size={14} className="text-gray-400" /> Tiempos de alerta
                            </h3>
                            <p className="text-xs text-gray-500 mb-4">Selecciona cuándo recibir las alertas antes de cada evento</p>
                            <div className="grid grid-cols-2 gap-2">
                                {ALERT_OPTIONS.map(opt => (
                                    <button key={opt.value} onClick={() => toggleAlert(opt.value)}
                                        className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                            settings.alerts.includes(opt.value)
                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                        }`}>
                                        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                            settings.alerts.includes(opt.value) ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                                        }`}>
                                            {settings.alerts.includes(opt.value) && <FiCheck size={10} className="text-white" />}
                                        </span>
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            {settings.alerts.length === 0 && (
                                <p className="text-xs text-amber-600 mt-2">Selecciona al menos un tiempo de alerta</p>
                            )}
                        </div>

                        {/* Sound */}
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FiVolume2 size={14} className="text-gray-400" />
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-800">Sonido de alerta</h3>
                                        <p className="text-xs text-gray-500">Reproduce un sonido al recibir la notificación</p>
                                    </div>
                                </div>
                                <button onClick={() => setSettings(prev => ({ ...prev, sound: !prev.sound }))}
                                    className={`w-11 h-6 rounded-full transition-colors relative ${settings.sound ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.sound ? 'left-[22px]' : 'left-0.5'}`} />
                                </button>
                            </div>
                        </div>

                        {/* Zoom link */}
                        <div className="bg-white rounded-xl border border-gray-200 p-5">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <FiLink size={14} className="text-gray-400" />
                                    <div>
                                        <h3 className="text-sm font-bold text-gray-800">Incluir enlace Zoom</h3>
                                        <p className="text-xs text-gray-500">Muestra el enlace de Zoom/clase en la notificación</p>
                                    </div>
                                </div>
                                <button onClick={() => setSettings(prev => ({ ...prev, showZoomLink: !prev.showZoomLink }))}
                                    className={`w-11 h-6 rounded-full transition-colors relative ${settings.showZoomLink ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.showZoomLink ? 'left-[22px]' : 'left-0.5'}`} />
                                </button>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                            <h3 className="text-sm font-bold text-gray-800 mb-3">Vista previa</h3>
                            <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm" style={{ borderLeft: '4px solid #f59e0b' }}>
                                <p className="text-xs font-bold text-gray-800">⏰ En {settings.alerts[0] || 30} min: MC</p>
                                <p className="text-xs text-gray-600 mt-0.5">
                                    MC DISEÑO ESTRUCTURAL - 6pm con Instructor Velasquez
                                    {settings.showZoomLink && <span className="text-blue-500"><br />Zoom: https://zoom.us/j/123...</span>}
                                </p>
                            </div>
                        </div>
                    </>
                )}

                {/* Save button */}
                <button onClick={handleSave} disabled={saving}
                    className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-indigo-200/50 transition">
                    {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        : saved ? <><FiCheck size={16} /> Guardado</>
                        : <><FiSave size={16} /> Guardar configuración</>}
                </button>
            </div>
        </div>
    );
};

export default CalendarSettingsPage;
